import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useApi from "../../helpers/apiHelper/requestHelper";
import {
  clearOAuthCodeFromUrl,
  clearOAuthSessionState,
  getOAuthCodeFromUrl,
  getOAuthRedirectUri,
  getStoredPermissionType,
  normalizeExchange,
} from "../../helpers/exchangeOAuth";
import {
  isOAuthBootstrapInFlight,
  wasOAuthCodeHandled,
} from "../../helpers/exchangeOAuthBootstrap";
import { fetchUser } from "../../helpers/helper";
import { storeUser } from "../../redux/users/usersSlice";
import {
  setConnectionFailMessage,
  setLoading,
  setResponseLoading,
  setShowConnectionFailModal,
} from "../../redux/loading/loadingSlice";
import { toastify } from "../../helpers/toast/toastify";

const getAuthToken = () => {
  const direct = localStorage.getItem("token");
  if (direct) return direct;

  try {
    const root = localStorage.getItem("persist:root");
    if (!root) return "";
    const parsed = JSON.parse(root);
    const userSlice = JSON.parse(parsed.user || "{}");
    return userSlice.token || "";
  } catch {
    return "";
  }
};

/**
 * Handles Binance/Bybit OAuth return (?code=) on /signin and /login.
 * Mounted from LoginLayout so it runs even when the outlet is hidden by loaders.
 */
const ExchangeOAuthCallback = () => {
  const oauthHandledRef = useRef(false);
  const api = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socialSignUp = useSelector((state) => state?.socialSignUp?.value);

  useEffect(() => {
    const code = getOAuthCodeFromUrl();
    if (!code || oauthHandledRef.current) return;

    if (isOAuthBootstrapInFlight() || wasOAuthCodeHandled(code)) return;

    oauthHandledRef.current = true;

    const token = getAuthToken();
    const exchange = normalizeExchange(localStorage.getItem("ExchangeClick"));
    const connectMode = localStorage.getItem("oauthConnectMode") === "connect";
    const returnTo = localStorage.getItem("oauthReturnTo") || "/";
    const allowSocialLogin =
      localStorage.getItem("AllowSocialLogin") === "true";

    dispatch(setLoading(true));
    dispatch(setResponseLoading(true));

    const finish = async () => {
      try {
        if (token || connectMode) {
          const response = await api("post", "users/loginWithByBit", {
            code,
            exchange,
            redirectUri: getOAuthRedirectUri(),
            permissionType: getStoredPermissionType(),
          });

          if (!response?.status) {
            clearOAuthCodeFromUrl();
            dispatch(setConnectionFailMessage(response?.message));
            dispatch(setShowConnectionFailModal(true));
            toastify(response?.message);
            return;
          }

          await fetchUser(api, dispatch, storeUser);
          clearOAuthCodeFromUrl();
          localStorage.removeItem("AllowSocialLogin");
          window.location.replace(returnTo);
          return;
        }

        if (!allowSocialLogin) {
          clearOAuthCodeFromUrl();
          toastify("Please log in before connecting your exchange.");
          return;
        }

        const memberNoLink =
          localStorage.getItem("memberNoLinkOAuth") === "true";

        const response = await api("post", "social/socialLogin", {
          code,
          exchange,
          ...(memberNoLink
            ? { memberNoLink: true }
            : { community: socialSignUp?.community }),
        });

        if (response?.status) {
          dispatch(storeUser(response?.data));
          localStorage.setItem("token", response?.data?.token);
          clearOAuthCodeFromUrl();
          localStorage.removeItem("AllowSocialLogin");

          if (memberNoLink) {
            navigate("/cmWithNoLink/dashboard");
            return;
          }

          const role = response?.data?.user?.role || "member";
          setTimeout(() => {
            navigate(`/${role}/dashboard`);
          }, 1500);
        } else {
          clearOAuthCodeFromUrl();
          localStorage.removeItem("AllowSocialLogin");
          toastify(response?.message);
        }
      } catch (error) {
        clearOAuthCodeFromUrl();
        console.error("ExchangeOAuthCallback -> error:", error);
        toastify(error?.message || "Exchange connection failed");
      } finally {
        clearOAuthSessionState();
        dispatch(setLoading(false));
        dispatch(setResponseLoading(false));
      }
    };

    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default ExchangeOAuthCallback;
